import dayjs from 'dayjs';

/**
 * Windows for the audit-trail links out of a register.
 *
 * Every register drills to the Journal **tab**, and that tab takes its window
 * from the Books shell and cannot widen it for a deep link. So a link has to
 * carry a window that contains the entry — otherwise the drawer opens over a
 * list the entry is not in, which is the dead-link failure these affordances
 * exist to avoid.
 *
 * Neither helper guesses. Where the caller holds the entry's own date, the
 * window is that day. Where it holds only a period, the window is that month —
 * which is enough because both period-driven entries are dated at the period
 * end: payroll stamps `periodEndDate(period)`, and a depreciation charge is
 * dated at the end of the period it charges.
 *
 * Both return `null` on input they cannot read. `DepreciationPeriod` and
 * `PayrollPeriod` are documented `YYYY-MM` but are bare `string` aliases, and a
 * form date is a string that can be empty — so nothing but the shape itself
 * stops a malformed value arriving. Unchecked, `dayjs` would answer
 * `Invalid Date` and that string would be *rendered into the URL*, sending a
 * reader of the ledger to a window that means nothing. A caller that gets
 * `null` drops the link instead: a dead click is a bad affordance, but a link
 * to the wrong month is a wrong answer about money.
 */

export type EntryWindow = { from: string; to: string };

/** `YYYY-MM-DD`. Shape only — `dayjs` decides whether the day exists. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** `YYYY-MM`. */
const ISO_MONTH = /^\d{4}-\d{2}$/;

/** The entry's own date — the exact answer, when the caller has it. */
export const entryWindowForDate = (date: string): EntryWindow | null => {
    if (!ISO_DATE.test(date)) {
        return null;
    }
    // Catches a well-shaped date that is not a real one (`2026-02-31`), which
    // `dayjs` would otherwise roll forward into March without complaint.
    const parsed = dayjs(date);
    if (!parsed.isValid() || parsed.format('YYYY-MM-DD') !== date) {
        return null;
    }

    return { from: date, to: date };
};

/** A `YYYY-MM` period, widened to the month that contains its period-end entry. */
export const entryWindowForPeriod = (period: string): EntryWindow | null => {
    if (!ISO_MONTH.test(period)) {
        return null;
    }
    const start = dayjs(`${period}-01`);
    // The round-trip is the real check. A month outside 1–12 satisfies the
    // shape and `isValid()` alike, because the underlying `Date` rolls it over
    // instead of refusing — `2026-13` becomes January **2027**, and the drill
    // would quietly scope to a window a year away from the entry.
    if (!start.isValid() || start.format('YYYY-MM') !== period) {
        return null;
    }

    return {
        from: start.format('YYYY-MM-DD'),
        to: start.endOf('month').format('YYYY-MM-DD'),
    };
};
